import { html, type Html, Page, View } from 'rune-ts';
import klass from './RecentlySold.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { ProductCardViewData } from '../../../features/ProductList/type';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { EXCLUDE_MAKER_MODULE } from '../../../shared/constant';
import { DocumentVisibilityTracker } from '../../../shared/util/DocumentVisibilityTracker';
import { pipe, take, toArray, uniqBy } from '@fxts/core';
import { getRecentlySoldProductsApi } from '../../../features/ProductList/api';
import { CoreUiS } from '@services/MarppleShop/features/Core/outbound/share.ts';
import { GlobalTimerManager } from '@services/MarppleShop/shared/util/timer';
import { createGoodsUrlBuilder } from '@services/MarppleShop/features/ProductList/util';
import { TabListView } from '@services/MarppleShop/shared/components/cells/TabList/TabListView.ts';
import {
  CardProductControllerListView,
  TProductCardViewDataWithIsNew,
} from '@services/MarppleShop/renderApp/pages/RecentlySold/CardProductControllerListView.ts';

export type RecentlySoldPageData = {
  recently_sold_products: ProductCardViewData[];
  layout_options: BaseLayoutOptions;

  /**
   * ms
   */
  polling_duration: number;
  limit: number;
};

class RecentlySoldPageBodyView extends View<RecentlySoldPageData> {
  private recentlySoldProductsWithIsNew: TProductCardViewDataWithIsNew[] =
    this.data.recently_sold_products.map((recentlySoldProduct) => ({
      ...recentlySoldProduct,
      isNew: false,
    }));

  private cardProductControllerListView: CardProductControllerListView = new CardProductControllerListView(
    this.recentlySoldProductsWithIsNew,
  );

  private visibility_tracker: DocumentVisibilityTracker;

  constructor(data: RecentlySoldPageData) {
    super(data);

    this.visibility_tracker = new DocumentVisibilityTracker(
      this.data.polling_duration,
      async (start_timestamp) => {
        const products = await getRecentlySoldProductsApi({
          last_ordered_at: this.data.recently_sold_products.at(0)?.ordered_at ?? new Date(start_timestamp),
        });

        const productsWithIsNew: TProductCardViewDataWithIsNew[] = products.map((product) => ({
          ...product,
          isNew: true,
        }));

        const nextRecentlySoldProductsWithIsNew = pipe(
          [...productsWithIsNew, ...this.recentlySoldProductsWithIsNew],
          uniqBy(({ id }) => id),
          take(this.data.limit),
          toArray,
        );

        this.data.recently_sold_products = nextRecentlySoldProductsWithIsNew;
        this.cardProductControllerListView.set(nextRecentlySoldProductsWithIsNew);
      },
    );
  }

  protected override onRender() {
    super.onRender();

    this.visibility_tracker.startTracking();

    const globalTimer = GlobalTimerManager.getInstance();
    globalTimer.start(1000);
  }

  protected override onUnmount() {
    this.visibility_tracker.stopTracking();

    const globalTimer = GlobalTimerManager.getInstance();
    globalTimer.stop();
  }

  protected override template() {
    const { is_mobile } = this.data.layout_options;

    return html`<div class="${klass.body}">
      <!-- title -->
      ${is_mobile
        ? new TabListView({
            tabs: [
              {
                tab_title: ET('mps2::recently_sold::title'),
                selected: true,
                href: createGoodsUrlBuilder().setPage('LATEST_SOLD').build(),
              },
              {
                tab_title: ET('mps2::section_title::section_tabs::creator_ranking'),
                selected: false,
                href: `/${ET.lang}/@/creator/ranking/realtime`,
              },
            ],
          })
        : html`<div class="${klass.page_title}">
            ${CoreUiS.makeRealTimeRankingTitle(is_mobile, 'realtime', ET('mps2::recently_sold::sub_title'))}
          </div>`}

      <!-- products -->
      <div class="${klass.product_cards_container}">${this.cardProductControllerListView}</div>
    </div>`;
  }
}

export class RecentlySoldPage extends Page<RecentlySoldPageData> {
  protected override template(): Html {
    const { layout_options } = this.data;

    const body = new RecentlySoldPageBodyView(this.data);

    return html`
      <div class="${klass.page} ${EXCLUDE_MAKER_MODULE}">
        ${new LayoutView(
          {
            body,
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            require_top_arrow_button: true,
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: 1,
            ...layout_options,
            full_width: layout_options.is_mobile,
          },
        )}
      </div>
    `;
  }
}
